import type { Action } from '../threads/action';
import type { DatabaseType } from '../types';
import { promptLoader } from './prompt-loader';

export type { DatabaseType };

/**
 * Gets the current database type from the prompt loader
 * @returns The database type string
 */
export function getDatabaseType(): DatabaseType {
	return promptLoader.getDatabaseType() as DatabaseType;
}

/**
 * Detects the database type from a tool ID prefix (e.g., 'mssql-662cba0bcea8_query' → 'mssql')
 * Falls back to the global database type from prompt loader if no match found.
 * @param toolId - The tool ID string
 * @returns The detected database type
 */
export function getDbTypeFromToolId(toolId?: string): DatabaseType {
	if (!toolId) return getDatabaseType();
	const prefix = toolId.split('-')[0]?.toLowerCase();
	// Normalize variant source types (e.g., mssql_bridge → mssql)
	if (prefix.startsWith('mssql')) return 'mssql';
	if (prefix.startsWith('postgres')) return 'postgresql';
	if (prefix.startsWith('mysql')) return 'mysql';
	if (prefix.startsWith('snowflake')) return 'snowflake';
	return getDatabaseType();
}

/**
 * Converts LIMIT syntax to TOP syntax for MSSQL
 * MSSQL doesn't support LIMIT keyword - it must use TOP
 * @param query - The SQL query to check
 * @param limit - The limit value to use
 * @returns The query with LIMIT converted to TOP
 */
export function convertLimitToTop(query: string, limit: number): string {
	if (!query || query.trim().length === 0) {
		return query;
	}

	// Remove LIMIT clause and add TOP after SELECT
	let modifiedQuery = query.replace(/\s*\bLIMIT\s+\d+\b/gi, '').trim();

	// Add TOP N after SELECT (handles SELECT DISTINCT as well)
	modifiedQuery = modifiedQuery.replace(/^\s*(SELECT)\s+(DISTINCT\s+)?/i, `$1 TOP ${limit} $2`);

	return modifiedQuery;
}

/**
 * Converts TOP syntax to LIMIT syntax for PostgreSQL/Snowflake
 * These databases don't support TOP keyword - they must use LIMIT
 * @param query - The SQL query to check
 * @returns The query with TOP removed (LIMIT will be added separately)
 */
export function convertTopToLimit(query: string): string {
	if (!query || query.trim().length === 0) {
		return query;
	}

	// Remove "TOP N" from query - LIMIT will be added at the end
	// Pattern: SELECT TOP number or SELECT TOP (number)
	let modifiedQuery = query.replace(/\bSELECT\s+TOP\s+\(?\d+\)?\s*/gi, 'SELECT ');

	return modifiedQuery;
}

/**
 * Ensures a SQL query has a row limiting clause to prevent large result sets
 * Only applies to SELECT queries - leaves INSERT, UPDATE, DELETE, etc. unchanged
 * Database-aware: Uses TOP for MSSQL, LIMIT for PostgreSQL/Snowflake/MySQL
 * Enforces a maximum limit to prevent excessive data retrieval
 * @param query - The SQL query to check
 * @param defaultLimit - Default limit to apply if none exists (default: 10)
 * @param maxLimit - Maximum allowed limit (default: 100)
 * @param dbType - Database type (optional, defaults to promptLoader's database type)
 * @returns The query with appropriate row limiting clause (if it's a SELECT query)
 */
export function ensureQueryLimit(
	query: string,
	defaultLimit: number = 10,
	maxLimit: number = 10,
	dbType?: DatabaseType
): string {
	if (!query || query.trim().length === 0) {
		return query;
	}

	// Get database type from parameter or prompt loader
	const databaseType = dbType || getDatabaseType();
	const isMssql = databaseType === 'mssql';

	let trimmedQuery = query.trim();

	// Only apply limits to SELECT queries
	// Check if the query is a SELECT statement (not INSERT, UPDATE, DELETE, CREATE, DROP, ALTER, etc.)
	const isSelectQuery = /^\s*SELECT\b/i.test(trimmedQuery) ||
		/^\s*WITH\b.*\bSELECT\b/is.test(trimmedQuery); // Also handle CTEs (WITH clause)

	if (!isSelectQuery) {
		// Not a SELECT query, return as-is
		return query;
	}

	// Remove any trailing semicolon for processing
	const hadSemicolon = trimmedQuery.endsWith(';');
	if (hadSemicolon) {
		trimmedQuery = trimmedQuery.slice(0, -1).trim();
	}

	if (isMssql) {
		// MSSQL: Use TOP syntax
		return ensureMssqlTop(trimmedQuery, defaultLimit, maxLimit, hadSemicolon);
	} else {
		// PostgreSQL/Snowflake/MySQL: Use LIMIT syntax
		return ensurePostgresLimit(trimmedQuery, defaultLimit, maxLimit, hadSemicolon);
	}
}

/**
 * Ensures MSSQL query has TOP clause for row limiting
 */
function ensureMssqlTop(query: string, defaultLimit: number, maxLimit: number, hadSemicolon: boolean): string {
	let trimmedQuery = query;

	// First, remove any LIMIT clauses (might be added by LLM incorrectly)
	trimmedQuery = trimmedQuery.replace(/\s*\bLIMIT\s+\d+\b/gi, '').trim();

	// Check if TOP already exists
	const topMatch = trimmedQuery.match(/\bSELECT\s+TOP\s+\(?\s*(\d+)\s*\)?/i);

	if (topMatch) {
		const existingTop = parseInt(topMatch[1], 10);

		// If existing TOP is within limits, keep it
		if (existingTop <= maxLimit) {
			if (hadSemicolon) {
				trimmedQuery += ';';
			}
			return trimmedQuery;
		}

		// If existing TOP exceeds maxLimit, replace it
		console.warn(`⚠️  Query TOP ${existingTop} exceeds maximum of ${maxLimit}. Reducing to ${maxLimit}...`);
		trimmedQuery = trimmedQuery.replace(/\bSELECT\s+TOP\s+\(?\s*\d+\s*\)?/i, `SELECT TOP ${maxLimit}`);

		if (hadSemicolon) {
			trimmedQuery += ';';
		}
		return trimmedQuery;
	}

	// No TOP exists, add it after SELECT (handle SELECT DISTINCT)
	trimmedQuery = trimmedQuery.replace(
		/^\s*(SELECT)\s+(DISTINCT\s+)?/i,
		`$1 TOP ${defaultLimit} $2`
	);

	if (hadSemicolon) {
		trimmedQuery += ';';
	}
	return trimmedQuery;
}

/**
 * Ensures PostgreSQL/Snowflake/MySQL query has LIMIT clause for row limiting
 */
function ensurePostgresLimit(query: string, defaultLimit: number, maxLimit: number, hadSemicolon: boolean): string {
	let trimmedQuery = query;

	// First, convert any TOP syntax to standard format (remove TOP, will add LIMIT)
	trimmedQuery = convertTopToLimit(trimmedQuery);

	// Remove any duplicate LIMIT clauses
	const limitMatches = trimmedQuery.match(/\bLIMIT\s+(\d+)\b/gi);

	if (limitMatches && limitMatches.length > 0) {
		if (limitMatches.length > 1) {
			// Multiple LIMIT clauses, remove all and add fresh one
			console.warn(`⚠️  Query had ${limitMatches.length} LIMIT clauses. Removing duplicates...`);
			trimmedQuery = trimmedQuery.replace(/\s*\bLIMIT\s+\d+\b/gi, '').trim();
		} else {
			// Single LIMIT exists - check if it exceeds maxLimit
			const existingLimitMatch = trimmedQuery.match(/\bLIMIT\s+(\d+)\b/i);
			if (existingLimitMatch) {
				const existingLimit = parseInt(existingLimitMatch[1], 10);

				if (existingLimit <= maxLimit) {
					if (hadSemicolon) {
						trimmedQuery += ';';
					}
					return trimmedQuery;
				}

				console.warn(`⚠️  Query LIMIT ${existingLimit} exceeds maximum of ${maxLimit}. Reducing to ${maxLimit}...`);
				trimmedQuery = trimmedQuery.replace(/\bLIMIT\s+\d+\b/i, `LIMIT ${maxLimit}`);

				if (hadSemicolon) {
					trimmedQuery += ';';
				}
				return trimmedQuery;
			}
		}
	}

	// Add LIMIT clause at the end
	trimmedQuery = `${trimmedQuery} LIMIT ${defaultLimit}`;

	if (hadSemicolon) {
		trimmedQuery += ';';
	}
	return trimmedQuery;
}

/**
 * Validates and fixes scalar subqueries to ensure they return exactly one row
 * Database-aware: Adds TOP 1 for MSSQL, LIMIT 1 for PostgreSQL/Snowflake/MySQL
 * Fixes subqueries used with scalar comparison operators (=, <, >, <=, >=, <>)
 *
 * @param query - The SQL query to validate
 * @param dbType - Database type (optional, defaults to promptLoader's database type)
 * @returns The query with fixed scalar subqueries
 */
export function fixScalarSubqueries(query: string, dbType?: DatabaseType): string {
	if (!query || query.trim().length === 0) {
		return query;
	}

	// Get database type from parameter or prompt loader
	const databaseType = dbType || getDatabaseType();
	const isMssql = databaseType === 'mssql';

	let modifiedQuery = query;
	let hasChanges = false;

	// Find all subqueries preceded by scalar comparison operators
	// Pattern: operator followed by (SELECT ...)
	const scalarOperatorPattern = /([=<>!]=?|<>)\s*\(\s*SELECT\s/gi;

	const matches = [...modifiedQuery.matchAll(scalarOperatorPattern)];

	// Process matches in reverse order to maintain string positions
	for (let i = matches.length - 1; i >= 0; i--) {
		const match = matches[i];
		const startPos = match.index! + match[0].length - 'SELECT '.length;

		// Find the matching closing parenthesis
		let parenDepth = 1;
		let endPos = startPos;
		let foundEnd = false;

		for (let j = startPos; j < modifiedQuery.length; j++) {
			const char = modifiedQuery[j];
			if (char === '(') parenDepth++;
			if (char === ')') {
				parenDepth--;
				if (parenDepth === 0) {
					endPos = j;
					foundEnd = true;
					break;
				}
			}
		}

		if (!foundEnd) continue;

		// Extract the subquery content
		const subquery = modifiedQuery.substring(startPos, endPos);

		if (isMssql) {
			// Check if it already has TOP
			if (/\bSELECT\s+TOP\s+\d+/i.test(subquery)) {
				continue;
			}
			// Add TOP 1 after SELECT for MSSQL
			const fixedSubquery = subquery.replace(/^\s*SELECT\s+/i, 'SELECT TOP 1 ');
			modifiedQuery =
				modifiedQuery.substring(0, startPos) +
				fixedSubquery +
				modifiedQuery.substring(endPos);
		} else {
			// Check if it already has LIMIT
			if (/\bLIMIT\s+\d+/i.test(subquery)) {
				continue;
			}
			// Add LIMIT 1 at the end for PostgreSQL/Snowflake/MySQL
			const fixedSubquery = subquery.trim() + ' LIMIT 1';
			modifiedQuery =
				modifiedQuery.substring(0, startPos) +
				fixedSubquery +
				modifiedQuery.substring(endPos);
		}

		hasChanges = true;
		const limitType = isMssql ? 'TOP 1' : 'LIMIT 1';
		console.warn(`⚠️  Fixed scalar subquery: added ${limitType} to prevent multiple row error`);
	}

	if (hasChanges) {
		const dbName = isMssql ? 'MSSQL' : 'PostgreSQL';
		console.log(`✓ Query validated and fixed for ${dbName} scalar subquery compatibility`);
	}

	return modifiedQuery;
}

/**
 * Converts question strings to Action objects format
 * Used for follow-up questions generated by LLM
 * @param questions - Array of question strings
 * @returns Array of Action objects
 */
export function convertQuestionsToActions(questions: string[]): Action[] {
	return questions.map((question: string, index: number) => ({
		id: `action_${index}_${Date.now()}`,
		name: question,
		type: 'next_question',
		question
	}));
}

/**
 * Calculates the size of a JSON object in bytes
 * @param obj - The object to measure
 * @returns Size in bytes
 */
export function getJsonSizeInBytes(obj: any): number {
	const jsonString = JSON.stringify(obj);
	return Buffer.byteLength(jsonString, 'utf8');
}

/**
 * Checks if a message exceeds the WebSocket size limit
 * @param message - The message object to check
 * @param maxSize - Maximum size in bytes (default: 1MB)
 * @returns Object with isValid flag and size information
 */
export function validateMessageSize(message: any, maxSize: number = 1048576): { isValid: boolean; size: number; maxSize: number } {
	const size = getJsonSizeInBytes(message);
	return {
		isValid: size <= maxSize,
		size,
		maxSize
	};
}

/**
 * Validates and fixes common SQL syntax errors without executing the query
 * This is a fast, synchronous function that catches common LLM mistakes
 * @param query - The SQL query to validate and fix
 * @param dbType - Database type (optional, defaults to promptLoader's database type)
 * @returns Object with fixed query and list of fixes applied
 */
export function validateAndFixSqlQuery(
	query: string,
	dbType?: DatabaseType
): { query: string; fixed: boolean; fixes: string[] } {
	if (!query || query.trim().length === 0) {
		return { query, fixed: false, fixes: [] };
	}

	const databaseType = dbType || getDatabaseType();
	// Note: isMssql can be used for database-specific fixes in the future
	const _isMssql = databaseType === 'mssql';

	let fixedQuery = query;
	const fixes: string[] = [];

	// ============================================
	// FIX 1: Move aggregate functions from WHERE to HAVING
	// ============================================
	// Detect patterns like: WHERE ... AND SUM(x) > 0 or WHERE ... AND COUNT(*) > 5
	const aggregateFunctions = ['SUM', 'COUNT', 'AVG', 'MIN', 'MAX'];
	const aggregateInWherePattern = new RegExp(
		`\\bWHERE\\b[^]*?\\b(${aggregateFunctions.join('|')})\\s*\\([^)]*\\)\\s*(?:=|>|<|>=|<=|<>|!=)`,
		'i'
	);

	if (aggregateInWherePattern.test(fixedQuery)) {
		// Find and extract aggregate conditions from WHERE
		// Pattern: AND/OR followed by aggregate condition
		const aggregateConditionPattern = new RegExp(
			`\\s+(AND|OR)\\s+((?:COALESCE\\s*\\(\\s*)?(?:${aggregateFunctions.join('|')})\\s*\\([^)]+\\)[^)]*(?:\\))?\\s*(?:=|>|<|>=|<=|<>|!=)\\s*[^\\s,)]+)`,
			'gi'
		);

		const aggregateConditions: string[] = [];
		let match;

		// Extract all aggregate conditions
		while ((match = aggregateConditionPattern.exec(fixedQuery)) !== null) {
			aggregateConditions.push(match[2].trim());
		}

		if (aggregateConditions.length > 0) {
			// Remove aggregate conditions from WHERE
			fixedQuery = fixedQuery.replace(aggregateConditionPattern, '');

			// Clean up any leftover "WHERE AND" or "WHERE OR" patterns
			fixedQuery = fixedQuery.replace(/\bWHERE\s+(AND|OR)\s+/gi, 'WHERE ');

			// Clean up empty WHERE clause
			fixedQuery = fixedQuery.replace(/\bWHERE\s+(GROUP\s+BY|ORDER\s+BY|HAVING|$)/gi, '$1');

			// Check if GROUP BY exists
			const hasGroupBy = /\bGROUP\s+BY\b/i.test(fixedQuery);

			if (hasGroupBy) {
				// Check if HAVING already exists
				const hasHaving = /\bHAVING\b/i.test(fixedQuery);
				const havingClause = aggregateConditions.join(' AND ');

				if (hasHaving) {
					// Add to existing HAVING
					fixedQuery = fixedQuery.replace(
						/\bHAVING\b/i,
						`HAVING ${havingClause} AND`
					);
				} else {
					// Insert HAVING after GROUP BY (before ORDER BY if exists)
					const orderByMatch = fixedQuery.match(/(\s+ORDER\s+BY\s+)/i);
					if (orderByMatch) {
						fixedQuery = fixedQuery.replace(
							orderByMatch[1],
							` HAVING ${havingClause}${orderByMatch[1]}`
						);
					} else {
						// Append HAVING at the end
						fixedQuery = fixedQuery.trimEnd() + ` HAVING ${havingClause}`;
					}
				}

				fixes.push(`Moved aggregate condition(s) from WHERE to HAVING: ${havingClause}`);
			}
		}
	}

	// ============================================
	// FIX 2: Balance parentheses
	// ============================================
	let openCount = 0;
	let closeCount = 0;
	let inString = false;
	let stringChar = '';

	for (let i = 0; i < fixedQuery.length; i++) {
		const char = fixedQuery[i];
		const prevChar = i > 0 ? fixedQuery[i - 1] : '';

		// Handle string literals
		if ((char === "'" || char === '"') && prevChar !== '\\') {
			if (!inString) {
				inString = true;
				stringChar = char;
			} else if (char === stringChar) {
				if (i + 1 < fixedQuery.length && fixedQuery[i + 1] === char) {
					i++; // Skip escaped quote
				} else {
					inString = false;
				}
			}
			continue;
		}

		if (!inString) {
			if (char === '(') openCount++;
			else if (char === ')') closeCount++;
		}
	}

	if (openCount > closeCount) {
		const missingClose = openCount - closeCount;
		fixedQuery = fixedQuery.trimEnd();
		const hasSemicolon = fixedQuery.endsWith(';');
		if (hasSemicolon) {
			fixedQuery = fixedQuery.slice(0, -1);
		}
		fixedQuery = fixedQuery + ')'.repeat(missingClose);
		if (hasSemicolon) {
			fixedQuery = fixedQuery + ';';
		}
		fixes.push(`Added ${missingClose} missing closing parenthesis(es)`);
	}

	// ============================================
	// FIX 3: ORDER BY in subqueries without TOP (MSSQL specific)
	// ============================================
	// MSSQL: ORDER BY is forbidden in subqueries unless TOP or OFFSET is present
	// Pattern: Find subqueries with ORDER BY but no TOP/OFFSET and add TOP
	const subqueryOrderByPattern = /\(\s*SELECT\s+(?!TOP\s)([^()]*?)\s+ORDER\s+BY\s+[^()]+\)/gi;
	let subqueryMatch;
	let subqueryFixed = false;

	while ((subqueryMatch = subqueryOrderByPattern.exec(fixedQuery)) !== null) {
		const fullMatch = subqueryMatch[0];
		// Check if this subquery already has TOP or OFFSET
		if (!/\bTOP\s+\d+/i.test(fullMatch) && !/\bOFFSET\b/i.test(fullMatch)) {
			// Add TOP 100 after SELECT in this subquery
			const fixedSubquery = fullMatch.replace(
				/\(\s*SELECT\s+/i,
				'(SELECT TOP 100 '
			);
			fixedQuery = fixedQuery.replace(fullMatch, fixedSubquery);
			subqueryFixed = true;
		}
	}

	if (subqueryFixed) {
		fixes.push('Added TOP to subquery with ORDER BY (MSSQL requirement)');
	}

	// ============================================
	// FIX 4: Clean up double spaces and formatting
	// ============================================
	const originalLength = fixedQuery.length;
	fixedQuery = fixedQuery.replace(/\s+/g, ' ').trim();
	if (fixedQuery.length !== originalLength) {
		// Don't add to fixes as this is just formatting
	}

	return {
		query: fixedQuery,
		fixed: fixes.length > 0,
		fixes
	};
}